import { Children, ReactElement } from 'react';

import { observer } from 'mobx-react-lite';
import styled from 'styled-components';

import { Activity, ActivityProps } from '@components/activity';
import { Avatar, AvatarColor } from '@components/avatar';
import { Card } from '@components/card';
import { IconButton } from '@components/icon-button';
import { IconOnboarding } from '@components/icons/onboarding';
import { IconStar } from '@components/icons/star';
import { IconStarSolid } from '@components/icons/star-solid';
import { Stack } from '@components/stack';
import { StatusLine, StatusLineProps } from '@components/status-line';
import { TagProps } from '@components/tag';
import { Text } from '@components/text';
import { useScopedTranslate } from '@locale/translate';
import { theme } from '@styles/theme';

export type ApplicantCardProps = {
  profile: ReactElement<ProfileProps>;
  active?: boolean;
  activity?: ReactElement<ActivityProps>;
  tags?: ReactElement<CardTagsProps>;
  statusLine?: StatusLineProps['variant'];
  onClick?(): void;
  onOnboardingClick?(): void;
  onFavoriteClick?(): void;
  isFavorited?: boolean;

  // When true, show no hover effects.
  flat?: boolean;
};

const ICON_SIZE = 16;

export const ApplicantCard = observer<ApplicantCardProps>(function ApplicantCard(props) {
  const { t } = useScopedTranslate(locale);
  const {
    activity,
    profile,
    statusLine,
    onClick,
    onOnboardingClick,
    onFavoriteClick,
    isFavorited = false,
    tags,
    active = false,
    flat = false,
  } = props;

  return (
    <ApplicantCardContainer $active={active} $flat={flat} onClick={onClick} space={3}>
      <Stack orientation="horizontal" space={2} justify="space-between">
        {statusLine && <StatusLine variant={statusLine} />}
        <Content>
          <Stack orientation="horizontal" justify="space-between">
            {profile}
            <Stack orientation="horizontal">
              {onOnboardingClick && (
                <OnboardingLink
                  variant="ghost-purple"
                  size="sm"
                  icon={<IconOnboarding size={ICON_SIZE} />}
                  stopPropagation
                  preventDefault
                  onClick={onOnboardingClick}
                />
              )}
              {onFavoriteClick && (
                <IconButton
                  tooltip={isFavorited ? t('removeFromFavorites') : t('addToFavorites')}
                  variant="ghost-purple"
                  size="sm"
                  icon={
                    isFavorited ? (
                      <IconStarSolid color="orange100" size={ICON_SIZE} />
                    ) : (
                      <IconStar size={ICON_SIZE} />
                    )
                  }
                  stopPropagation
                  preventDefault
                  onClick={onFavoriteClick}
                />
              )}
            </Stack>
          </Stack>
          {activity}
          {tags}
        </Content>
      </Stack>
    </ApplicantCardContainer>
  );
});

const ApplicantCardContainer = styled(Card)<{ $active: boolean; $flat: boolean }>((p) => ({
  '.status-line': {
    height: 'auto',
  },
  ...(!p.$flat && {
    ':hover': {
      boxShadow: theme.shadow.low,
    },
  }),

  ...(p.$active && {
    border: `0.125rem solid ${theme.color.elementBlue100} !important`,
    margin: '-0.0625rem',
  }),
}));

const Content = styled(Stack).attrs({
  space: 2,
  orientation: 'vertical',
})({
  minWidth: 0,
  flex: 1,
});

const OnboardingLink = styled(IconButton)({
  width: '2rem',
  height: '2rem',
});

type ProfileProps = {
  name: string;
  avatar?: string;
  detail?: string;
  avatarBackground?: AvatarColor;
};

export const ApplicantCardProfile = observer<ProfileProps>(function Profile(props) {
  const { name, avatar, detail, avatarBackground } = props;

  return (
    <ProfileContainer>
      <Avatar name={name} src={avatar} size="lg" color={avatarBackground} />
      <UserInfo>
        <Text ellipsis font="bold14" color="purple100" text={name} />
        {detail && <Text ellipsis font="regular12" color="purple60" text={detail} />}
      </UserInfo>
    </ProfileContainer>
  );
});

type CardActivityChild = ReactElement<CardActivityItemProps | null> | boolean;
type CardActivityProps = {
  children: CardActivityChild | Array<CardActivityChild>;
};

export const ApplicantCardActivity = observer<CardActivityProps>(function CardActivity(props) {
  const children = Children.toArray(props.children).filter(Boolean);
  if (!children.length) return null;

  return (
    <Stack orientation="horizontal" space={1} wrap>
      {children}
    </Stack>
  );
});

type CardActivityItemProps = Omit<ActivityProps, 'small'>;

export const ApplicantCardActivityItem = observer<CardActivityItemProps>(
  function ApplicantCardActivityItem(props) {
    return <Activity small {...props} />;
  }
);

type CardTagsProps = {
  children: ReactElement<TagProps> | Array<ReactElement<TagProps>>;
};
export const ApplicantCardTags = observer<CardTagsProps>(function CardTags(props) {
  const children = Children.toArray(props.children).filter(Boolean);
  if (!children.length) return null;

  return (
    <Stack orientation="horizontal" space={1} wrap>
      {children}
    </Stack>
  );
});

const ProfileContainer = styled(Stack).attrs({
  space: 2,
  orientation: 'horizontal',
  align: 'center',
})({
  minWidth: 0,
  flexGrow: 1,
});

const UserInfo = styled.div({
  overflow: 'hidden',
});

const locale = {
  en: {
    addToFavorites: 'Add to favourites',
    removeFromFavorites: 'Remove from favourites',
  },
  is: {
    addToFavorites: 'Bæta við uppáhalds',
    removeFromFavorites: 'Fjarlægja úr uppáhalds',
  },
};
